<template>
    <div class="navigation-bar" :class="{ scrolled, sticky }">
        <div>
            <slot name="left" />
        </div>

        <slot name="middle">
            <h1>
                {{ title }}
            </h1>
        </slot>

        <div>
            <slot name="right" />
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Prop,Vue } from "vue-property-decorator";

@Component
export default class NavigationBar extends Vue {
    @Prop({ default: "", type: String })
    title!: string;

    @Prop({ default: true, type: Boolean })
    sticky!: boolean;

    scrolled = false;
    scrollElement!: HTMLElement | null;

    getScrollElement(element: HTMLElement | null = null): HTMLElement {
        if (!element) {
            element = this.$el as HTMLElement;
        }

        const style = window.getComputedStyle(element);
        if (
            style.overflowY == "scroll" ||
            style.overflow == "scroll" ||
            style.overflow == "auto" ||
            style.overflowY == "auto"
        ) {
            return element;
        } else {
            if (!element.parentElement) {
                return document.documentElement;
            }
            return this.getScrollElement(element.parentElement);
        }
    }

    addListener() {
        if (this.scrollElement) {
            return;
        }
        this.scrollElement = this.getScrollElement();
        if (this.scrollElement === document.documentElement) {
            window.addEventListener("scroll", this.onScroll, { passive: true });
        } else {
            this.scrollElement.addEventListener("scroll", this.onScroll, { passive: true });
        }
    }

    mounted() {
        if (this.sticky) {
            this.addListener();
        }
    }

    activated() {
        // fix for element not yet in dom
        window.setTimeout(() => {
            if (this.sticky) {
                this.addListener();
                this.onScroll()
            }
        }, 500);
    }

    deactivated() {
        if (!this.scrollElement) {
            return;
        }
        if (this.scrollElement === document.documentElement) {
            window.removeEventListener("scroll", this.onScroll);
        } else {
            this.scrollElement.removeEventListener("scroll", this.onScroll);
        }
        this.scrollElement = null;
    }

    onScroll() {
        const scroll = this.scrollElement!.scrollTop;
        if (scroll > 50) {
            this.scrolled = true;
        } else if (scroll < 15) {
            this.scrolled = false;
        }
    }
}
</script>

<style lang="scss">
@use '~@restofrit/scss/base/text-styles.scss';

.navigation-bar {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: 10px;
    background: white;
    background: var(--color-current-background, white);
    z-index: 11;
    margin: 0 calc(-1 * var(--rf-horizontal-padding, 40px));
    margin-top: calc(-1 * var(--rf-vertical-padding, 20px) - var(--rf-safe-area-top, 0px));
    padding: var(--rf-safe-area-top, 0px) var(--rf-horizontal-padding, 40px) 0 var(--rf-horizontal-padding, 40px);
    height: 60px;
    -webkit-app-region: drag;

    @media (min-width: 800px) {
        // Some extra margin on non-mobile devices
        margin-top: calc(-1 * var(--rf-vertical-padding, 20px) + 20px - var(--rf-safe-area-top, 0px));
    }

    &.sticky {
        position: sticky;
        top: 0;
    }

    > div {
        display: flex;
        flex-direction: row;
        align-items: center;

        &:first-child {
            > * {
                margin: 0 10px;

                &:first-child {
                    margin-left: 0;
                }
            }
        }

        &:last-child {
            justify-content: flex-end;

            > * {
                margin: 0 10px;

                &:last-child {
                    margin-right: 0;
                }
            }
        }
    }

    > h1 {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        opacity: 0;
        transition: opacity 0.2s;
        @extend .style-h4;
    }

    &.scrolled {
        box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.05);
        > h1 {
            opacity: 1;
        }
    }
}
</style>
